/*
  File: src/pages/HomePage.jsx
  Purpose: Assemble homepage layout with hero, events scroller, categories, posters, and live TV grid.
  Routing: Navigates to /watch for streaming and /watch?autostart=match for quick start.
*/
import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Navbar from '../components/layout/Navbar';
import Footer from '../components/layout/Footer';
import Hero from '../components/home/Hero';
import StartStreamModal from '../components/Stream/StartStreamModal';
import EventsScroller from '../components/home/EventsScroller';
import { StreamingProvider, useStreaming } from '../contexts/StreamingContext';

const MATCH_TYPE = 'Football';

function HomePageContent() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const homeFlagSrc = `${process.env.PUBLIC_URL}/FCBayern.png`;
  const awayFlagSrc = `${process.env.PUBLIC_URL}/psg.png`;

  const API_BASE = (process.env.REACT_APP_API_BASE || '/dev');
  const buildMatchUrl = useCallback((path) => {
    const base = String(API_BASE || '').replace(/\/$/, '');
    const url = new URL(`${base}${path}`, window.location.origin);
    url.searchParams.set('match_type', MATCH_TYPE);
    return url.toString();
  }, [API_BASE]);
  const [matches, setMatches] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [starting, setStarting] = useState(false);
  const [startError, setStartError] = useState('');
  
  // Use the streaming context instead of creating a new service
  const { startStream, streamingService, streamSessionId } = useStreaming();

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError('');
      try {
        const res = await fetch(buildMatchUrl('/matches'), { headers: { accept: 'application/json' } });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();
        const items = Array.isArray(data?.items) ? data.items : [];
        if (!cancelled) setMatches(items);
      } catch (e) {
        if (!cancelled) setError('Failed to load matches');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [API_BASE, buildMatchUrl]);

  // Handle scroll-to functionality when matches are loaded
  useEffect(() => {
    const scrollToId = searchParams.get('scrollTo');
    if (scrollToId && matches.length > 0 && !loading) {
      // Small delay to ensure DOM is rendered
      const timeoutId = setTimeout(() => {
        const element = document.querySelector(`[data-item-id="${scrollToId}"]`);
        if (element) {
          element.scrollIntoView({ 
            behavior: 'smooth', 
            block: 'nearest',
            inline: 'center'
          });
        }
      }, 100);
      return () => clearTimeout(timeoutId);
    }
  }, [matches, loading, searchParams]);

  const eventCards = useMemo(() => {
    const s3ToHttp = (url) => {
      try {
        if (typeof url !== 'string') return url;
        if (url.startsWith('http://') || url.startsWith('https://')) return url;
        if (!url.startsWith('s3://')) return url;
        const without = url.replace('s3://', '');
        const slash = without.indexOf('/');
        if (slash <= 0) return url;
        const bucket = without.slice(0, slash);
        const key = without.slice(slash + 1);
        return `https://${bucket}.s3.amazonaws.com/${key}`;
      } catch { return url; }
    };
    return matches.map(m => {
      // Build custom_labels structure from new API (team-keyed arrays), and capture team logos
      let custom_labels = null;
      try {
        const keys = Object.keys(m || {});
        const teamKeys = keys.filter(k => Array.isArray(m[k]) && m[k].every(x => x && typeof x === 'object' && 'label' in x && 'color' in x));
        const logoKeys = keys.filter(k => typeof m[k] === 'string' && /^(s3:\/\/|https?:\/\/)/i.test(m[k]));
        if (teamKeys.length > 0) {
          const teams = teamKeys.map(teamKey => ({
            team: String(teamKey || '').toLowerCase(),
            custom_labels: (m[teamKey] || []).map(x => ({ label: x.label, color: x.color })),
          }));
          const logos = {};
          logoKeys.forEach(k => {
            const raw = m[k];
            let tk = String(k || '').toLowerCase();
            if (teamKeys.includes(k)) return; // skip arrays
            // Normalize keys like psg_logo, psg-log, psg_log -> psg
            const base = tk.replace(/[_-](logo|log)$/i, '');
            logos[base] = s3ToHttp(raw);
          });
          // Also consider explicit fields
          if (m.home_team_logo_url) logos.home = s3ToHttp(m.home_team_logo_url);
          if (m.away_team_logo_url) logos.away = s3ToHttp(m.away_team_logo_url);
          custom_labels = { teams, logos };
        } else if (m.custom_labels) {
          custom_labels = m.custom_labels;
        }
      } catch { /* ignore */ }
      return ({
        id: m.id,
        title: m.display_title || m.name,
        status: m.status || 'Scheduled',
        thumb: m.thumbnail_url,
        video_s3_url: m.video_s3_url,
        custom_labels,
      });
    });
  }, [matches]);

  // Check if there's a live match
  const liveMatch = useMemo(() => {
    return matches.find(m => String(m.status || '').toLowerCase() === 'live');
  }, [matches]);

  const handlePlay = useCallback(async (item) => {
    try {
      setStartError('');
      setStarting(true);

      // Check if there's currently a live match and stop its stream first
      try {
        const liveMatchResponse = await fetch(buildMatchUrl('/matches/live'), {
          headers: { 'accept': 'application/json' }
        });
        if (liveMatchResponse.ok) {
          const liveData = await liveMatchResponse.json();
          if (liveData.live_match && liveData.live_match.id !== item.id) {
            console.log('Stopping previous live match stream:', liveData.live_match.id);
            // Stop the current match stream before starting new one
            try {
              await streamingService.stopStream('match', liveData.live_match.stream_id);
              console.log('Previous match stream stopped');
            } catch (stopError) {
              console.warn('Failed to stop previous stream:', stopError.message);
              // Continue anyway - force flag will handle it
            }
          }
        }
      } catch (e) {
        console.warn('Failed to check for live match:', e.message);
        // Continue anyway
      }

      // Defensive: ask streaming service to stop any running match stream
      try { await streamingService.stopStream('match'); } catch (_) {}

      // Wait for the stream channel to fully stop before starting a new one
      const waitUntilStopped = async (type = 'match', timeoutMs = 15000, pollMs = 800) => {
        const deadline = Date.now() + timeoutMs;
        while (Date.now() < deadline) {
          try {
            const st = await streamingService.getStatus(type);
            const s = String(st?.status || '').toLowerCase();
            const isLive = st?.ivs_state === 'LIVE' || s === 'live' || s === 'streaming' || st?.running === true;
            if (!isLive) return true;
          } catch (_) { /* ignore and keep waiting */ }
          await new Promise(r => setTimeout(r, pollMs));
        }
        return false;
      };
      await waitUntilStopped('match', 12000, 700);

      // Determine source from item.video_s3_url
      const src = item?.video_s3_url || '';
      let opts = { force: true, waitForLive: true, debug: true };
      if (typeof src === 'string' && src.startsWith('s3://')) {
        // Parse s3://bucket/key
        const without = src.replace('s3://', '');
        const slash = without.indexOf('/');
        if (slash > 0) {
          opts.bucket = without.slice(0, slash);
          opts.key = without.slice(slash + 1);
        } else {
          throw new Error('Invalid S3 URL');
        }
      } else if (typeof src === 'string' && (src.startsWith('http://') || src.startsWith('https://'))) {
        opts.sourceUrl = src;
      } else {
        throw new Error('This event has no video assigned yet');
      }

      // Persist custom labels and match_id for the match in sessionStorage so /watch can restore even if state is lost
      try {
        if (item?.custom_labels) sessionStorage.setItem('match_custom_labels', JSON.stringify(item.custom_labels));
        if (item?.id) sessionStorage.setItem('current_match_id', String(item.id));
      } catch { }

      // Start stream for the new match using context (forceRestart=true ensures clean switching)
      console.log('Starting stream for match:', item.id);
      let startResp;
      try {
        startResp = await startStream('match', true); // Use context method with forceRestart
        if (!startResp?.success) {
          console.log('Context start failed, trying service directly with options');
          await streamingService.startStream('match', opts);
        }
      } catch (e) {
        // Handle transient IVS/FFmpeg output open failures by retrying once after ensuring channel is stopped
        console.warn('Start stream error, retrying once after stop/wait:', e?.message || e);
        try { await streamingService.stopStream('match'); } catch (_) {}
        await waitUntilStopped('match', 12000, 700);
        await streamingService.startStream('match', opts);
      }
      
      try {
        await streamingService.waitForLive(45000, 1500, 'match');
      } catch (_) { /* tolerate; Watch page will continue waiting */ }
      
      // Get stream session id from context response or context state, fallback to service
      let sid = startResp?.stream_session_id || streamSessionId || '';
      if (!sid) {
        try {
          const st = await streamingService.getStatus('match');
          sid = st?.stream_session_id || '';
        } catch (_) {}
      }
      
      // If we don't have session ID, wait a bit for it
      const deadline = Date.now() + 20000;
      while (!sid && Date.now() < deadline) {
        try {
          const st = await streamingService.getStatus('match');
          if (st?.stream_session_id) { sid = st.stream_session_id; break; }
        } catch (_) {}
        await new Promise(r => setTimeout(r, 800));
      }

      // Set this match as live in the database (this will stop any other live matches)
      try {
        const response = await fetch(buildMatchUrl('/matches/live/start'), {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'accept': 'application/json'
          },
          body: JSON.stringify({
            id: item.id,
            stream_id: sid
          })
        });
        
        if (!response.ok) {
          console.warn('Failed to update match status in database');
        } else {
          const result = await response.json();
          console.log('Match started in database:', result.started);
          if (result.stopped_ids && result.stopped_ids.length > 0) {
            console.log('Stopped previous live matches in database:', result.stopped_ids);
          }
        }
      } catch (e) {
        console.warn('Failed to update match status:', e.message);
      }

      // Navigate to watch page with the correct type and match_id
      const matchIdParam = item?.id ? `&match_id=${encodeURIComponent(item.id)}` : '';
      navigate(`/watch?type=match${matchIdParam}`, { state: { pendingLive: true, streamId: sid || undefined, customLabels: item?.custom_labels || null } });
    } catch (e) {
      setStartError(e?.message || 'Failed to start stream');
    } finally {
      setStarting(false);
    }
  }, [navigate, streamingService, API_BASE]);

  return (
    <div className="min-h-screen flex flex-col pt-14 sm:pt-16">
      <Navbar />

      <main className="flex-1">
        <Hero 
          homeFlagSrc={homeFlagSrc} 
          awayFlagSrc={awayFlagSrc} 
          hasLiveMatch={!!liveMatch}
          liveMatch={liveMatch}
          onWatch={() => {
            // If there's a live match, navigate to it; otherwise use first available match
            const targetMatch = liveMatch || matches[0];
            if (targetMatch?.id) {
              try {
                sessionStorage.setItem('current_match_id', String(targetMatch.id));
                if (targetMatch.custom_labels) {
                  sessionStorage.setItem('match_custom_labels', JSON.stringify(targetMatch.custom_labels));
                }
              } catch { }
              navigate(`/watch?type=match&match_id=${encodeURIComponent(targetMatch.id)}`);
            } else {
              navigate('/watch?type=match');
            }
          }} 
        />

        {startError && (
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mb-2 text-sm text-red-300">{startError}</div>
        )}
        <EventsScroller
          files={eventCards}
          onSelect={handlePlay}
          formatLabel={(card) => card.title}
        />
        <StartStreamModal isVisible={starting} isProcessing={starting} streamType="match" />
      </main>
      <Footer />
    </div>
  );
}

// Wrapper component with StreamingProvider
export default function HomePage() {
  const API_BASE = (process.env.REACT_APP_API_BASE || '/dev');
  const streamingBaseUrl = `${API_BASE}/streaming`;
  
  return (
    <StreamingProvider baseUrl={streamingBaseUrl} defaultType="match">
      <HomePageContent />
    </StreamingProvider>
  );
}
